import { FC, ReactNode } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useIntl } from 'react-intl'

import Dashboard from 'layouts/Dashboard'
import HelpdeskTabs from 'components/Helpdesk/HelpdeskTabs'
import { useCanReportFault } from 'components/Helpdesk/useCanReportFault'
import CreateReservation from 'components/CommonRoomReservations/CreateReservation'

interface HelpdeskLayoutProps {
  // Master list rendered in the left column on desktop (faults / reservations).
  navigationComponent?: ReactNode
  // Root path of the section, e.g. '/faults'.
  rootPath: string
  // Route patterns rendered full-width on desktop (forms, chat).
  singleColumnPaths?: string[]
}

// Layout for the Helpdesk section ("Panel Klienta 3.0" redesign).
// Desktop: HelpDesk topbar with tabs + optional master-detail columns.
// Mobile: header with tabs on the root path, full-screen subpages elsewhere.
const HelpdeskLayout: FC<HelpdeskLayoutProps> = ({
  navigationComponent,
  rootPath,
  singleColumnPaths = []
}) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const { canReportFault } = useCanReportFault()

  const isSingleColumn = singleColumnPaths.some((pattern) => matchPath(pattern, pathname))

  const actionButtonSx = {
    backgroundColor: theme.r4r.navy,
    color: '#fff',
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '13px',
    borderRadius: 0,
    padding: '11px 18px',
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    ':hover': {
      backgroundColor: theme.r4r.ink,
      boxShadow: 'none'
    }
  } as const

  const actionSlot =
    rootPath === '/faults' && canReportFault ? (
      <Button onClick={() => navigate('/faults/report/add')} sx={actionButtonSx} fullWidth={!isDesktop}>
        {formatMessage({ id: 'helpdesk.reportFault', defaultMessage: '+ Zgłoś usterkę' })}
      </Button>
    ) : rootPath === '/common-room-reservations' ? (
      <CreateReservation buttonSx={actionButtonSx} fullWidth={!isDesktop} />
    ) : null

  if (isDesktop) {
    return (
      <Dashboard noHeader disableGutters>
        <Box sx={{ backgroundColor: theme.r4r.greyBg, minHeight: '100vh' }}>
          <Box sx={{ maxWidth: '1200px', px: '36px', pt: '28px', pb: '64px' }}>
            <Typography
              sx={{ fontSize: '24px', fontWeight: 700, lineHeight: '28.8px', color: theme.r4r.navy }}>
              {formatMessage({ id: 'defectsList.helpDesk', defaultMessage: 'HelpDesk' })}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '20px',
                pt: '20px'
              }}>
              <HelpdeskTabs />
              {actionSlot}
            </Box>
            <Box sx={{ pt: '24px' }}>
              {navigationComponent && !isSingleColumn ? (
                <Box sx={{ display: 'flex', gap: '28px', alignItems: 'flex-start' }}>
                  <Box sx={{ width: '440px', flexShrink: 0, position: 'sticky', top: '24px' }}>
                    {navigationComponent}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Outlet />
                  </Box>
                </Box>
              ) : isSingleColumn ? (
                <Box
                  sx={{
                    maxWidth: '760px',
                    mx: 'auto',
                    backgroundColor: '#fff',
                    boxShadow: theme.r4r.cardShadow,
                    p: '28px'
                  }}>
                  <Outlet />
                </Box>
              ) : (
                <Outlet />
              )}
            </Box>
          </Box>
        </Box>
      </Dashboard>
    )
  }

  if (pathname === rootPath) {
    return (
      <Dashboard noHeader>
        <Box sx={{ backgroundColor: theme.r4r.greyBg, minHeight: '100vh', pb: '24px' }}>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 700,
              color: theme.r4r.navy,
              textAlign: 'center',
              pt: '18px',
              pb: '14px'
            }}>
            {formatMessage({ id: 'defectsList.helpDesk', defaultMessage: 'HelpDesk' })}
          </Typography>
          <Box sx={{ px: '16px' }}>
            <HelpdeskTabs />
          </Box>
          {actionSlot && <Box sx={{ px: '16px', pt: '16px' }}>{actionSlot}</Box>}
          <Box sx={{ px: '16px', pt: '16px' }}>
            <Outlet />
          </Box>
        </Box>
      </Dashboard>
    )
  }

  return <Outlet />
}

export default HelpdeskLayout
